import { useEffect, useRef, useState } from "react";
import { X, Zap } from "lucide-react";
import { playRewardedAd, isAdsConfigured } from "@/lib/rewardedAds";

/**
 * AdModal — wrapper around the AdsBitVex rewarded video SDK.
 *
 * Behavior per product spec:
 *   • When REACT_APP_ADSBITVEX_APP_ID is configured we fire the SDK
 *     immediately on open, render NO loading UI, and let the SDK take over
 *     the screen with its own full-screen player. The Promise resolves when
 *     the user closes the ad after watching — that's when we fire onComplete.
 *     If no ads are available or the SDK errors, we simply call onClose
 *     (the SDK itself surfaces "no ads right now" to the user).
 *   • When no App ID is set (local dev / previews) we fall back to a
 *     5-second demo countdown so the game loop still works end-to-end.
 *
 * There is no watchdog and no retry — we trust the SDK.
 */
export default function AdModal({ open, onComplete, onClose, reason = "Watch a short ad to continue" }) {
  const [demoCountdown, setDemoCountdown] = useState(5);
  const startedRef = useRef(false);
  const demoTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setDemoCountdown(5);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    if (!isAdsConfigured()) {
      demoTimerRef.current = setInterval(() => {
        setDemoCountdown((n) => (n > 0 ? n - 1 : 0));
      }, 1000);
    }

    playRewardedAd()
      .then(() => {
        if (demoTimerRef.current) clearInterval(demoTimerRef.current);
        onComplete?.();
      })
      .catch(() => {
        if (demoTimerRef.current) clearInterval(demoTimerRef.current);
        onClose?.();
      });

    return () => {
      if (demoTimerRef.current) clearInterval(demoTimerRef.current);
    };
  }, [open, onComplete, onClose]);

  if (!open) return null;

  // Production path — SDK renders its own full-screen player, we stay invisible.
  if (isAdsConfigured()) return null;

  // Demo fallback UI (dev only).
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="ad-modal-backdrop"
    >
      <div className="w-full max-w-md border-2 border-[#EAEB18] bg-[#0B0C10] p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#94A3B8] hover:text-white"
          data-testid="ad-modal-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">
          [ SPONSORED · REWARDED AD ]
        </div>
        <h3 className="font-heading font-black text-xl text-white mb-2">
          {reason}
        </h3>
        <p className="text-xs text-[#94A3B8] mb-6">
          Free tier plays a short ad. Upgrade to <span className="text-[#EAEB18]">DareDino Pro</span> to skip ads and unlock unlimited rooms.
        </p>
        <div className="relative aspect-video border border-[#00F0FF]/40 bg-gradient-to-br from-[#050505] to-[#1a1a2e] mb-4 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(45deg, #00F0FF 0 2px, transparent 2px 20px)" }} />
          <div className="text-center z-10 px-4">
            <Zap size={40} className="mx-auto mb-2 text-[#EAEB18] animate-pulse" strokeWidth={2.5} />
            <div className="font-mono-arcade text-[10px] tracking-widest text-[#00F0FF] uppercase">
              Playing your reward ad
            </div>
            <div className="font-heading font-black text-5xl text-white mt-3" data-testid="ad-countdown">
              {demoCountdown || "GO"}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-[#94A3B8]/60 text-center uppercase tracking-widest">
          {demoCountdown > 0 ? `Reward unlocks in ${demoCountdown}s` : "Reward unlocked — loading..."}
        </div>
      </div>
    </div>
  );
}
